import { Component, Input, forwardRef, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

export interface AudioRecording {
  fileName: string;
  fileUrl: string;
  blob: Blob;
  duration: number;
}

export enum RecordingState {
  IDLE = 'idle',
  RECORDING = 'recording',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  ERROR = 'error'
}

@Component({
  selector: 'app-form-audio-recorder',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="form-section audio-recorder">
      <label [attr.for]="id" [class.required-field]="required">{{ label }}</label>
      
      <div class="audio-recorder-controls">
        <!-- 錄音控制按鈕 -->
        <div class="recording-controls" *ngIf="!audioRecording">
          <button 
            type="button" 
            class="btn btn-primary recording-btn"
            [disabled]="!isRecordingSupported"
            (click)="toggleRecording()"
            [class.recording]="recordingState === 'recording'"
            [class.paused]="recordingState === 'paused'">
            <i class="fas" [ngClass]="{
              'fa-microphone': recordingState === 'idle',
              'fa-pause': recordingState === 'recording',
              'fa-play': recordingState === 'paused'
            }"></i>
            <span *ngIf="recordingState === 'idle'">{{ 'form.audio_recorder.start_recording' | translate }}</span>
            <span *ngIf="recordingState === 'recording'">{{ 'form.audio_recorder.pause_recording' | translate }}</span>
            <span *ngIf="recordingState === 'paused'">{{ 'form.audio_recorder.resume_recording' | translate }}</span>
          </button>
          
          <button 
            type="button" 
            class="btn btn-secondary"
            *ngIf="recordingState !== 'idle'"
            (click)="stopRecording()">
            <i class="fas fa-stop"></i>
            {{ 'form.audio_recorder.stop_recording' | translate }}
          </button>
          
          <!-- 錄音狀態指示 -->
          <div class="recording-status" *ngIf="recordingState !== 'idle'">
            <div class="recording-indicator" [class.active]="recordingState === 'recording'">
              <i class="fas fa-circle"></i>
            </div>
            <span class="recording-time">{{ formatTime(recordingTime) }}</span>
          </div>
        </div>
        
        <!-- 已錄製音訊顯示 -->
        <div class="recorded-audio" *ngIf="audioRecording">
          <div class="audio-info">
            <i class="fas fa-file-audio"></i>
            <span class="audio-name">{{ audioRecording.fileName }}</span>
            <span class="audio-duration">{{ formatTime(audioRecording.duration) }}</span>
          </div>
          
          <!-- 音訊播放控制 -->
          <div class="audio-playback">
            <button 
              type="button" 
              class="btn btn-outline-primary btn-sm"
              (click)="togglePlayback()">
              <i class="fas" [ngClass]="{
                'fa-play': !isPlaying,
                'fa-pause': isPlaying
              }"></i>
              {{ isPlaying ? ('form.audio_recorder.pause' | translate) : ('form.audio_recorder.play' | translate) }}
            </button>
            
            <div class="playback-progress" *ngIf="isPlaying || playbackProgress > 0">
              <div class="progress">
                <div class="progress-bar" [style.width.%]="playbackProgress"></div>
              </div>
              <span class="progress-time">{{ formatTime(playbackCurrentTime) }}</span>
            </div>
          </div>
          
          <!-- 重新錄音按鈕 -->
          <button 
            type="button" 
            class="btn btn-warning btn-sm"
            (click)="resetRecording()">
            <i class="fas fa-redo"></i>
            {{ 'form.audio_recorder.re_record' | translate }}
          </button>
        </div>
        
        <!-- 錯誤訊息 -->
        <div class="alert alert-danger" *ngIf="errorMessage">
          <i class="fas fa-exclamation-triangle"></i>
          {{ errorMessage }}
        </div>
        
        <!-- 不支援錄音的提示 -->
        <div class="alert alert-warning" *ngIf="!isRecordingSupported">
          <i class="fas fa-exclamation-circle"></i>
          {{ 'form.audio_recorder.browser_not_supported' | translate }}
        </div>
      </div>
      
      <!-- 配置說明 -->
      <small class="form-text text-muted" *ngIf="placeholder">
        {{ placeholder }}
      </small>
    </div>
    
    <!-- 隱藏的 audio 元素用於播放 -->
    <audio #audioPlayer (ended)="onPlaybackEnded()" (timeupdate)="onTimeUpdate($event)"></audio>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormAudioRecorderComponent),
      multi: true,
    },
  ],
  styleUrls: ['total-component.scss']
})
export class FormAudioRecorderComponent implements ControlValueAccessor, OnDestroy, OnInit {
  @Input() label: string = '';
  @Input() id: string = 'audio-recorder';
  @Input() name: string = 'audio-recorder';
  @Input() required: boolean = false;
  @Input() placeholder: string = '';
  @Input() maxDuration: number = 300; // 5分鐘預設
  @Input() minDuration: number = 1; // 1秒預設
  @Input() audioQuality: 'high' | 'medium' | 'low' = 'medium';
  @Input() audioFormat: 'mp3' | 'wav' = 'wav';

  // 錄音相關屬性
  recordingState: RecordingState = RecordingState.IDLE;
  mediaRecorder: MediaRecorder | null = null;
  audioChunks: Blob[] = [];
  recordingTime: number = 0;
  recordingTimer: any = null;
  isRecordingSupported: boolean = false;
  errorMessage: string = '';

  // 播放相關屬性
  audioRecording: AudioRecording | null = null;
  audioPlayer: HTMLAudioElement | null = null;
  isPlaying: boolean = false;
  playbackProgress: number = 0;
  playbackCurrentTime: number = 0;

  onChange = (value: any) => {};
  onTouched = () => {};

  constructor(private i18nService: I18nService) {}

  ngOnInit() {
    // 設定預設 label（如果沒有提供）
    if (!this.label) {
      this.label = this.i18nService.translate('form.audio_recorder.label');
    }
    this.checkRecordingSupport();
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private checkRecordingSupport(): void {
    this.isRecordingSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }

  async toggleRecording(): Promise<void> {
    try {
      if (this.recordingState === RecordingState.IDLE) {
        await this.startRecording();
      } else if (this.recordingState === RecordingState.RECORDING) {
        this.pauseRecording();
      } else if (this.recordingState === RecordingState.PAUSED) {
        this.resumeRecording();
      }
    } catch (error) {
      this.handleError(this.i18nService.translate('form.audio_recorder.recording_operation_failed') + ': ' + (error as Error).message);
    }
  }

  private async startRecording(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: this.getAudioSampleRate(),
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        } 
      });

      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: this.getSupportedMimeType()
      });

      this.audioChunks = [];
      this.recordingTime = 0;

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.handleRecordingComplete();
      };

      this.mediaRecorder.start();
      this.recordingState = RecordingState.RECORDING;
      this.startTimer();
      this.errorMessage = '';

    } catch (error) {
      if ((error as any).name === 'NotAllowedError') {
        this.handleError(this.i18nService.translate('form.audio_recorder.microphone_permission_required'));
      } else {
        this.handleError(this.i18nService.translate('form.audio_recorder.microphone_access_failed') + ': ' + (error as Error).message);
      }
    }
  }

  private pauseRecording(): void {
    if (this.mediaRecorder && this.recordingState === RecordingState.RECORDING) {
      this.mediaRecorder.pause();
      this.recordingState = RecordingState.PAUSED;
      this.stopTimer();
    }
  }

  private resumeRecording(): void {
    if (this.mediaRecorder && this.recordingState === RecordingState.PAUSED) {
      this.mediaRecorder.resume();
      this.recordingState = RecordingState.RECORDING;
      this.startTimer();
    }
  }

  stopRecording(): void {
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
      this.stopTimer();
    }
  }

  private handleRecordingComplete(): void {
    if (this.recordingTime < this.minDuration) {
      this.handleError(this.i18nService.translate('form.audio_recorder.recording_too_short', { minDuration: this.minDuration.toString() }));
      this.resetRecording();
      return;
    }

    const audioBlob = new Blob(this.audioChunks, { 
      type: this.getSupportedMimeType() 
    });

    this.audioRecording = {
      fileName: this.i18nService.translate('form.audio_recorder.recording_filename') + '_' + new Date().toISOString().slice(0, 19).replace(/:/g, '-') + '.' + this.audioFormat,
      fileUrl: URL.createObjectURL(audioBlob),
      blob: audioBlob,
      duration: this.recordingTime
    };

    this.recordingState = RecordingState.COMPLETED;
    this.onChange(this.audioRecording);
    this.onTouched();
  }

  resetRecording(): void {
    this.cleanup();
    this.audioRecording = null;
    this.recordingState = RecordingState.IDLE;
    this.recordingTime = 0;
    this.errorMessage = '';
    this.onChange(null);
  }

  togglePlayback(): void {
    if (!this.audioRecording) return;

    if (!this.audioPlayer) {
      this.audioPlayer = new Audio(this.audioRecording.fileUrl);
    }

    if (this.isPlaying) {
      this.audioPlayer.pause();
      this.isPlaying = false;
    } else {
      this.audioPlayer.play();
      this.isPlaying = true;
    }
  }

  onPlaybackEnded(): void {
    this.isPlaying = false;
    this.playbackProgress = 0;
    this.playbackCurrentTime = 0;
  }

  onTimeUpdate(event: any): void {
    if (this.audioPlayer && this.audioRecording) {
      this.playbackCurrentTime = this.audioPlayer.currentTime;
      this.playbackProgress = (this.audioPlayer.currentTime / this.audioRecording.duration) * 100;
    }
  }

  private startTimer(): void {
    this.recordingTimer = setInterval(() => {
      this.recordingTime++;
      if (this.recordingTime >= this.maxDuration) {
        this.stopRecording();
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
  }

  private getAudioSampleRate(): number {
    switch (this.audioQuality) {
      case 'high': return 48000;
      case 'medium': return 44100;
      case 'low': return 22050;
      default: return 44100;
    }
  }

  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/wav'
    ];
    
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    
    return 'audio/webm'; // fallback
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private handleError(message: string): void {
    this.errorMessage = message;
    this.recordingState = RecordingState.ERROR;
    this.cleanup();
  }

  private cleanup(): void {
    this.stopTimer();
    if (this.mediaRecorder) {
      this.mediaRecorder.stream?.getTracks().forEach(track => track.stop());
      this.mediaRecorder = null;
    }
    if (this.audioPlayer) {
      this.audioPlayer.pause();
      this.audioPlayer = null;
    }
    this.audioChunks = [];
    this.isPlaying = false;
    this.playbackProgress = 0;
    this.playbackCurrentTime = 0;
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {
    this.audioRecording = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可以根據需要實作禁用狀態
  }
}